import { Component, OnInit, Input, ViewChild, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  MessageCircle,
  Send,
  Paperclip,
  X,
  Eye,
  FileText,
  File,
  Image as ImageIcon,
} from 'lucide-angular';
import {
  InsuranceService,
  CreateInsuranceMessageDto,
  InsuranceDocument,
} from '../../../../core/services/insurance.service';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { AngularPhoneNumberInput } from 'angular-phone-number-input';
import {
  ProviderAutocompleteComponent,
  Provider,
} from './provider-autocomplete.component';
import { MessageResponsesComponent } from './message-responses.component';
import { SwalService } from '../../../../core/services/swal.service';
import { InsuranceMessage } from '../../../../core/interfaces/insurance.interface';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { loadCurrentUser } from '../../../../store/auth/auth.actions';
import { DocumentService, CreateDocumentDto } from '../../../../core/services/document.service';
import { PassportService } from '../../../../core/services/passport.service';
import { HttpEventType } from '@angular/common/http';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-messages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    AngularPhoneNumberInput,
    ProviderAutocompleteComponent,
    MessageResponsesComponent,
  ],
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
})
export class MessagesComponent implements OnInit {
  @Input() beneficiaries: Array<{ fullname: string; passportNumber: string }> = [];
  @Input() contractId: string = '';
  @Input() insurerExternalId?: string; // Ajout de l'externalId de l'insurer
  @Input() contract: any;
  readonly MessageCircle = MessageCircle;
  readonly Send = Send;
  readonly Paperclip = Paperclip;
  readonly X = X;
  readonly Eye = Eye;
  readonly FileText = FileText;
  readonly File = File;
  readonly ImageIcon = ImageIcon;

  messages: Array<{
    id: string;
    sender: string;
    content: string;
    timestamp: string;
    isRead: boolean;
    type: 'incoming' | 'outgoing';
  }> = [];

  documents: {
    url: string;
    filename: string;
    bucketName: string;
    sizeBytes: number;
  }[] = [];

  newMessage: string = '';
  loading = false;
  refreshing = false; // Nouvelle propriété pour le rafraîchissement
  showNewMessageForm = false;
  messageTypesLoading = false;
  selectedFileName: string = '';
  selectedFile: File | null = null;
  selectedFiles: File[] = [];
  selectedFileNames: string[] = [];

  // File upload tracking
  uploadedFiles: Array<{
    id: string; // Unique ID for tracking
    file: File;
    progress: number; // 0-100
    isUploading: boolean;
    isUploaded: boolean;
    uploadedDocumentId?: string;
    uploadedUrl?: string;
    error?: string;
    previewUrl?: string; // For images
  }> = [];

  // Form data
  newMessageForm = {
    messageType: '',
    messageActorType: 'PATIENT' as
      | 'PATIENT'
      | 'INSURED'
      | 'INSURER'
      | 'HEALTH_PROFESSIONAL',
    destinaryType: 'INSURER' as
      | 'INSURER'
      | 'HEALTH_PROFESSIONAL'
      | 'PATIENT'
      | 'INSURED'
      | 'RADIOLOGY'
      | 'PHYSICIAN'
      | 'PHARMACY'
      | 'MEDICAL_STRUCTURE'
      | 'LABORATORY',
    beneficiaryPasspport: '',
    content: '',
    healthProEyoneExternalId: '',
    healthProEyoneName: '',
    healthProType: '',
  };

  // Sample data for dropdowns
  serviceTypes: Array<{ id: string; name: string }> = [];

  messageTypes: Array<{ label: string; type: string }> = [];

  // Document types
  documentTypes: Array<{ _id: string; code: string; label: string }> = [];
  insuranceDocumentTypeId: string | null = null;

  // Loading state for message sending
  sendingMessage = false;

  // Validation modal for non-conventionnés providers
  showValidationModal = false;
  validatingProvider = false;
  validationData = {
    providerName: '',
    providerType: ''
  };
  pendingSearchTerm = '';
  
  // Validation errors for modal
  validationErrors = {
    providerName: '',
    providerType: ''
  };

  // Configuration des pays pour le composant de téléphone
  preferredCountries: string[] = ['SN', 'GA', 'CI']; // Sénégal et Gabon uniquement

  // Discussion management
  discussions: Array<{
    id: string;
    title: string;
    lastMessage: string;
    insurerName: string;
    timestamp: string;
    unreadCount: number;
    type: string;
  }> = [];
  selectedDiscussionId: string | null = null;
  selectedDiscussion: any = null;
  private pendingDiscussionSelectionId: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasMoreDiscussions = false;
  loadingMore = false;

  // Reference to discussions container for scrolling
  discussionsContainerRef?: HTMLElement;

  currentUser: any;

  @ViewChild(ProviderAutocompleteComponent) providerAutocomplete!: ProviderAutocompleteComponent;

  constructor(
    private insuranceService: InsuranceService,
    private referentielService: ReferentielService,
    private swalService: SwalService,
    private store: Store,
    private documentService: DocumentService,
    private passportService: PassportService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // S'abonner aux changements du currentUser
    this.store.select(selectCurrentUser).subscribe((user) => {
      if (user) {
        this.currentUser = user;
      }
    });

    this.loadMessages();
    this.loadFormData();
    this.loadMessageTypes();
    this.loadDocumentTypes();
  }

  loadMessages(preferredDiscussionId?: string): void {
    this.loading = true;
    this.currentPage = 1; // Reset to first page when loading messages

    if (this.contractId) {
      // Load messages from backend
      this.insuranceService
        .getContractMessages(this.contractId, {
          page: this.currentPage,
          limit: this.pageSize,
        })
        .subscribe({
          next: (response) => {
            if (response && response.messages) {
              this.messages = response.messages.map(
                (msg: InsuranceMessage) => ({
                  id: msg._id,
                  sender: this.getSenderName(msg),
                  content: this.formatMessageContent(msg),
                  timestamp: msg.createdAt,
                  isRead: msg.isRead,
                  type: this.determineMessageType(msg),
                })
              );

              // Trier les messages par timestamp (plus récent en premier)
              this.messages.sort(
                (a, b) =>
                  new Date(b.timestamp).getTime() -
                  new Date(a.timestamp).getTime()
              );

              // Créer les discussions à partir des messages
              this.createDiscussionsFromMessages(response.messages, false);

              // Update pagination info
              this.totalItems = response.pagination.total;
              this.totalPages = response.pagination.totalPages;
              this.hasMoreDiscussions = this.currentPage < this.totalPages;
              this.applyDiscussionSelection(preferredDiscussionId);
            }
            this.loading = false;
            this.refreshing = false; // Désactiver l'état de rafraîchissement
          },
          error: (error) => {
            console.error('Error loading messages:', error);
            this.loading = false;
            this.refreshing = false; // Désactiver l'état de rafraîchissement même en cas d'erreur
          },
        });
    } else {
      // Simulation de chargement des messages
      setTimeout(() => {
        this.messages = [];
        this.loading = false;
        this.refreshing = false; // Désactiver l'état de rafraîchissement
      }, 1000);
    }
  }

  loadMoreDiscussions(): void {
    if (this.loadingMore || !this.hasMoreDiscussions) return;

    this.loadingMore = true;
    this.currentPage++;

    if (this.contractId) {
      // Load more messages from backend with pagination
      this.insuranceService
        .getContractMessages(this.contractId, {
          page: this.currentPage,
          limit: this.pageSize,
        })
        .subscribe({
          next: (response) => {
            if (response && response.messages) {
              // Add new messages to existing ones
              const newMessages = response.messages.map(
                (msg: InsuranceMessage) => ({
                  id: msg._id,
                  sender: this.getSenderName(msg),
                  content: this.formatMessageContent(msg),
                  timestamp: msg.createdAt,
                  isRead: msg.isRead,
                  type: this.determineMessageType(msg),
                })
              );

              // Éviter les doublons en vérifiant les IDs
              const existingMessageIds = new Set(
                this.messages.map((m) => m.id)
              );
              const uniqueNewMessages = newMessages.filter(
                (m) => !existingMessageIds.has(m.id)
              );

              this.messages = [...this.messages, ...uniqueNewMessages];

              // Trier les messages par timestamp (plus récent en premier)
              this.messages.sort(
                (a, b) =>
                  new Date(b.timestamp).getTime() -
                  new Date(a.timestamp).getTime()
              );

              // Create additional discussions from new messages
              this.createDiscussionsFromMessages(response.messages, true);

              // Update pagination info
              this.totalItems = response.pagination.total;
              this.totalPages = response.pagination.totalPages;
              this.hasMoreDiscussions = this.currentPage < this.totalPages;

              // Scroll to bottom after adding new discussions
              this.scrollToBottom();
            }
            this.loadingMore = false;
          },
          error: (error) => {
            console.error('Error loading more messages:', error);
            this.loadingMore = false;
            this.currentPage--; // Revert page increment on error
          },
        });
    } else {
      // Simulation de chargement de plus de messages
      setTimeout(() => {
        this.loadingMore = false;
      }, 1000);
    }
  }

  private createDiscussionsFromMessages(
    messages: InsuranceMessage[],
    append: boolean = false
  ): void {
    // Grouper les messages par type pour créer des discussions
    const discussionMap = new Map<string, InsuranceMessage[]>();

    messages.forEach((message) => {
      const discussionKey = message._id;
      if (!discussionMap.has(discussionKey)) {
        discussionMap.set(discussionKey, []);
      }
      discussionMap.get(discussionKey)!.push(message);
    });

    // Créer les nouvelles discussions
    const newDiscussions = Array.from(discussionMap.entries()).map(
      ([type, msgs]) => {
        const sortedMessages = msgs.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const lastMessage = sortedMessages[0];
        const unreadCount = msgs.filter(
          (msg) => !msg.isRead && this.determineMessageType(msg) === 'incoming'
        ).length;

        return {
          id: lastMessage._id,
          title: this.getDiscussionTitle(type, lastMessage.messageType.label),
          lastMessage: this.formatMessageContent(lastMessage),
          timestamp: lastMessage.createdAt,
          insurerName: lastMessage.destinataries[0]?.organism?.name,
          unreadCount,
          type: type,
        };
      }
    );

    if (append) {
      // Ajouter les nouvelles discussions à la liste existante
      // Éviter les doublons en vérifiant les IDs
      const existingIds = new Set(this.discussions.map((d) => d.id));
      const uniqueNewDiscussions = newDiscussions.filter(
        (d) => !existingIds.has(d.id)
      );

      // Préserver la position de scroll actuelle
      const discussionsContainer = document.querySelector(
        '.discussions-scroll'
      ) as HTMLElement;
      const currentScrollTop = discussionsContainer
        ? discussionsContainer.scrollTop
        : 0;

      this.discussions = [...this.discussions, ...uniqueNewDiscussions];

      // Trier par timestamp (plus récent en premier)
      this.discussions.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      // Restaurer la position de scroll
      if (discussionsContainer) {
        setTimeout(() => {
          discussionsContainer.scrollTop = currentScrollTop;
        }, 50);
      }
    } else {
      // Remplacer complètement la liste (comportement par défaut)
      this.discussions = newDiscussions;
    }
  }

  private applyDiscussionSelection(preferredDiscussionId?: string | null): void {
    if (!this.discussions.length) {
      this.selectedDiscussionId = null;
      this.selectedDiscussion = null;
      this.pendingDiscussionSelectionId = null;
      return;
    }

    const targetDiscussionId =
      preferredDiscussionId ??
      this.pendingDiscussionSelectionId ??
      this.selectedDiscussionId ??
      this.discussions[0]?.id;

    const targetDiscussion =
      this.discussions.find((discussion) => discussion.id === targetDiscussionId) ??
      this.discussions[0];

    this.pendingDiscussionSelectionId = null;

    if (!targetDiscussion) {
      return;
    }

    if (this.selectedDiscussionId !== targetDiscussion.id) {
      this.selectDiscussion(targetDiscussion.id);
      return;
    }

    this.selectedDiscussionId = targetDiscussion.id;
    this.selectedDiscussion = targetDiscussion;
    this.loadDocuments(targetDiscussion.id);
  }

  private getDiscussionTitle(type: string, label: string): string {
    switch (type) {
      case 'CARE_REQUEST':
        return 'Demandes de prise en charge';
      case 'QUESTION':
        return 'Questions';
      case 'INSURANCE_IDENTIFICATION_NUM_MAJ':
        return 'Mise à jour numéro';
      case 'IDENTIFICATION_PHOTO_MAJ':
        return 'Mise à jour photo';
      default:
        return label;
    }
  }

  selectDiscussion(discussionId: string): void {
    this.selectedDiscussionId = discussionId;
    // Trouver et définir la discussion sélectionnée
    this.selectedDiscussion =
      this.discussions.find((d) => d.id === discussionId) || null;
    // Filtrer les messages pour cette discussion
    this.filterMessagesByDiscussion(discussionId);
    this.loadDocuments(discussionId);
  }

  private filterMessagesByDiscussion(discussionId: string): void {
    // Recharger les messages pour cette discussion spécifique
    this.loading = true;

    this.insuranceService
      .getContractMessages(this.contractId, {
        page: 1,
        limit: this.pageSize,
      })
      .subscribe({
        next: (response) => {
          if (response && response.messages) {
            // Filtrer les messages par type de discussion
            const filteredMessages = response.messages.filter(
              (msg: InsuranceMessage) => msg.messageType.code === discussionId
            );

            this.messages = filteredMessages.map((msg: InsuranceMessage) => ({
              id: msg._id,
              sender: this.getSenderName(msg),
              content: this.formatMessageContent(msg),
              timestamp: msg.createdAt,
              isRead: msg.isRead,
              type: this.determineMessageType(msg),
            }));

            // Trier les messages par timestamp (plus récent en premier)
            this.messages.sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
            );
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading discussion messages:', error);
          this.loading = false;
        },
      });
  }

  private getSenderName(message: InsuranceMessage): string {
    // Déterminer le nom de l'expéditeur basé sur les destinataires
    if (message.destinataries && message.destinataries.length > 0) {
      const destinataire = message.destinataries[0];
      if (destinataire.messageActorType.insured) {
        return 'Vous';
      } else if (destinataire.organism) {
        return destinataire.organism.name;
      }
    }
    return 'Système';
  }

  private formatMessageContent(message: InsuranceMessage): string {
    let content = message.content;

    // Ajouter le type de message si ce n'est pas une question simple
    if (message.messageType.code !== 'QUESTION') {
      content = `[${message.messageType.label}] ${content}`;
    }

    // Ajouter les informations sur les documents si présents
    if (message.documents && message.documents.length > 0) {
      const fileNames = message.documents.map((doc) => doc.fileName).join(', ');
      content += ` (Fichiers: ${fileNames})`;
    }

    return content;
  }

  private determineMessageType(
    message: InsuranceMessage
  ): 'incoming' | 'outgoing' {
    // Déterminer si c'est un message entrant ou sortant
    if (message.destinataries && message.destinataries.length > 0) {
      const destinataire = message.destinataries[0];
      if (destinataire.messageActorType.patient) {
        return 'outgoing';
      }
    }
    return 'incoming';
  }

  hasDocuments(message: any): boolean {
    return message.documents && message.documents.length > 0;
  }

  getDocuments(message: any): any[] {
    return message.documents || [];
  }

  openDocument(document: any): void {
    if (document.url) {
      window.open(document.url, '_blank');
    }
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || !this.selectedDiscussionId) return;

    // Activer l'état de chargement
    this.sendingMessage = true;

    this.insuranceService
      .sendResponse(this.selectedDiscussionId, this.newMessage)
      .subscribe({
        next: (response) => {
          this.sendingMessage = false;
          this.newMessage = ''; // Vider le champ de message

          this.refreshDataAfterNewMessage(this.selectedDiscussionId);
        },
        error: (error) => {
          console.error("Erreur lors de l'envoi du message:", error);
          this.sendingMessage = false;
          this.swalService.error('Erreur', "Erreur lors de l'envoi du message");
        },
      });
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (diffInHours < 48) {
      return 'Hier';
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
      });
    }
  }

  getUnreadCount(): number {
    return this.messages.filter((msg) => !msg.isRead && msg.type === 'incoming')
      .length;
  }

  // New message form methods
  loadFormData(): void {
    // Sample data - in real app, this would come from services
    // Use input beneficiaries if provided, otherwise use sample data
    if (this.beneficiaries.length === 0) {
      this.beneficiaries = [];
    }

    this.serviceTypes = [];
  }

  loadMessageTypes(): void {
    this.messageTypesLoading = true;
    this.insuranceService.getMessageTypes().subscribe({
      next: (response) => {
        if (response) {
          this.messageTypes = response;
        }
        this.messageTypesLoading = false;
        this.refreshing = false; // Désactiver l'état de rafraîchissement
      },
      error: (error) => {
        console.error('Error loading message types:', error);
        this.messageTypesLoading = false;
        this.refreshing = false; // Désactiver l'état de rafraîchissement même en cas d'erreur
      },
    });
  }

  loadDocumentTypes(): void {
    this.passportService.getAllDocumentTypes().subscribe({
      next: (response) => {
        if (response && Array.isArray(response)) {
          this.documentTypes = response;
          // Trouver l'ID du type avec le code INSURANCE_DOCUMENT
          const insuranceDocType = response.find(
            (docType: any) => docType.code === 'INSURANCE_DOCUMENT'
          );
          if (insuranceDocType) {
            this.insuranceDocumentTypeId = insuranceDocType._id;
            console.log('Insurance document type ID found:', this.insuranceDocumentTypeId);
          } else {
            console.warn('INSURANCE_DOCUMENT type not found in document types');
          }
        }
      },
      error: (error) => {
        console.error('Error loading document types:', error);
      },
    });
  }

  openNewMessageForm(): void {
    this.showNewMessageForm = true;
  }

  cancelNewMessage(): void {
    this.showNewMessageForm = false;
    this.resetForm();
  }

  clearProvider(): void {
    this.newMessageForm.healthProEyoneExternalId = '';
    this.newMessageForm.healthProEyoneName = '';
    // Also clear the autocomplete component selection
    if (this.providerAutocomplete) {
      this.providerAutocomplete.clearSelection();
    }
  }

  onProviderSelected(provider: any): void {
    this.newMessageForm.healthProEyoneExternalId =
      provider.eyoneExternalId || provider.id;
    this.newMessageForm.healthProEyoneName = provider.name;

    // Handle healthProType for non-conventionnés providers
    if (provider.healthProType) {
      this.newMessageForm.healthProType = provider.healthProType;
    }
  }

  onProviderTypeChanged(type: 'conventionnes' | 'non_conventionnes'): void {
    // When switching from "Conventionnés" to "non conventionné", clear the provider fields
    if (type === 'non_conventionnes') {
      this.newMessageForm.healthProEyoneExternalId = '';
      this.newMessageForm.healthProEyoneName = '';
      // Also clear the validation data if any
      this.validationData = { providerName: '', providerType: '' };
      this.pendingSearchTerm = '';
    }
  }

  onValidationRequested(event: { searchTerm: string }): void {
    this.pendingSearchTerm = event.searchTerm;
    this.validationData = {
      providerName: event.searchTerm,
      providerType: ''
    };
    this.showValidationModal = true;
  }

  closeValidationModal(): void {
    this.showValidationModal = false;
    this.validatingProvider = false;
    this.validationData = { providerName: '', providerType: '' };
    this.validationErrors = { providerName: '', providerType: '' };
    this.pendingSearchTerm = '';
  }

  validateProvider(): void {
    // Clear previous errors
    this.validationErrors = { providerName: '', providerType: '' };
    
    // Validate form
    if (!this.isModalFormValid()) {
      return;
    }
    
    // Set loading state
    this.validatingProvider = true;
    
    // Set the provider data directly in the form
    this.newMessageForm.healthProEyoneName = this.validationData.providerName.trim();
    this.newMessageForm.healthProType = this.validationData.providerType;
    this.newMessageForm.healthProEyoneExternalId = ''; // Keep it null for non-conventionnés
    
    // Submit the message first (while validationData is still available)
    this.submitMessageWithValidatedProvider();
  }
  
  isModalFormValid(): boolean {
    let isValid = true;
    
    // Validate provider name
    if (!this.validationData.providerName.trim()) {
      this.validationErrors.providerName = 'Le nom du prestataire est obligatoire';
      isValid = false;
    }
    
    // Validate provider type
    if (!this.validationData.providerType) {
      this.validationErrors.providerType = 'Le type de prestataire est obligatoire';
      isValid = false;
    }
    
    return isValid;
  }

  private submitMessageWithValidatedProvider(): void {
    if (!this.contractId) return;

    // Activer l'animation de chargement
    this.sendingMessage = true;

    // D'abord uploader tous les fichiers, puis envoyer le message
    this.uploadAllFiles()
      .then(() => {
        // Tous les fichiers sont uploadés, maintenant envoyer le message
    // Préparer le contenu du message (optionnel pour CARE_REQUEST et REFUND_REQUEST)
    let messageContent = (this.newMessageForm.content || '').trim();

    // Si c'est une demande de changement de numéro de téléphone, extraire le numéro sans l'indicatif
    if (
      this.newMessageForm.messageType === 'INSURANCE_IDENTIFICATION_NUM_MAJ'
    ) {
      messageContent = this.extractPhoneNumber(this.newMessageForm.content);
    }

    // Debug: Log form data before creating message
    console.log('Form data before submission:', {
      healthProEyoneName: this.newMessageForm.healthProEyoneName,
      healthProType: this.newMessageForm.healthProType,
      healthProEyoneExternalId: this.newMessageForm.healthProEyoneExternalId
    });
    console.log('Validation data:', this.validationData);

    // Create message data according to backend DTO - use validationData directly to ensure values are preserved
    // Collecter tous les _id des fichiers uploadés dans un tableau simple
    const uploadedDocumentIds = this.uploadedFiles
      .filter(f => f.isUploaded && f.uploadedDocumentId)
      .map(f => f.uploadedDocumentId!);
    
    console.log('Uploaded document IDs array:', uploadedDocumentIds);

    const messageData: CreateInsuranceMessageDto = {
      content: messageContent || '',
      messageType: this.newMessageForm.messageType as any,
      messageActorType: this.newMessageForm.messageActorType,
      destinaryType: this.newMessageForm.destinaryType,
      beneficiaryPasspport:
        this.newMessageForm.beneficiaryPasspport ||
        this.currentUser?.passportNumber ||
        undefined,
      healthProEyoneExternalId:
        this.newMessageForm.healthProEyoneExternalId || undefined,
      healthProEyoneName: this.validationData.providerName || this.newMessageForm.healthProEyoneName || undefined,
      healthProType: this.validationData.providerType || this.newMessageForm.healthProType || undefined,
      documents: uploadedDocumentIds.length > 0 ? uploadedDocumentIds : undefined,
    };

    // Les fichiers sont déjà uploadés séparément, donc on envoie un JSON
    // Call the insurance service to send the message as JSON
    this.insuranceService
      .createMessage(this.contractId, messageData)
      .subscribe({
        next: (response) => {
          // Désactiver l'animation de chargement
          this.sendingMessage = false;

          // Close the validation modal
          this.closeValidationModal();

          // Afficher un message de succès
          this.swalService.success('Succès', 'Message envoyé avec succès !');

          // Rafraîchir la liste des discussions et messages
          this.refreshDataAfterNewMessage(response?._id);

          // Reset form and hide it
          this.resetForm();
        },
        error: (error) => {
          // Désactiver l'animation de chargement
          this.sendingMessage = false;

          console.error('Error sending message:', error);

          // Afficher un toast d'erreur
          let errorMessage = "Erreur lors de l'envoi du message";
          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.swalService.error('Erreur', errorMessage);
        },
          });
      })
      .catch((error) => {
        // Erreur lors de l'upload des fichiers
        this.sendingMessage = false;
        console.error('Error uploading files:', error);
        this.swalService.error('Erreur', 'Erreur lors de l\'upload des fichiers. Veuillez réessayer.');
      });
  }

  resetForm(): void {
    // Libérer les URLs de preview
    this.uploadedFiles.forEach(file => {
      if (file.previewUrl && file.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });

    this.newMessageForm = {
      messageType: '',
      messageActorType: 'PATIENT' as
        | 'PATIENT'
        | 'INSURED'
        | 'INSURER'
        | 'HEALTH_PROFESSIONAL',
      destinaryType: 'INSURER' as
        | 'INSURER'
        | 'HEALTH_PROFESSIONAL'
        | 'PATIENT'
        | 'INSURED'
        | 'RADIOLOGY'
        | 'PHYSICIAN'
        | 'PHARMACY'
        | 'MEDICAL_STRUCTURE'
        | 'LABORATORY',
      beneficiaryPasspport: '',
      content: '',
      healthProEyoneExternalId: '',
      healthProEyoneName: '',
      healthProType: '',
    };
    this.selectedFileName = '';
    this.selectedFile = null;
    this.selectedFiles = [];
    this.selectedFileNames = [];
    this.uploadedFiles = [];
  }

  /**
   * Validates provider selection for REFUND_REQUEST
   * - For conventionnés: must have selected a provider (healthProEyoneExternalId)
   * - For non conventionnés: must have either selected a provider OR typed a search term for validation
   */
  private isRefundRequestProviderValid(): boolean {
    if (this.newMessageForm.messageType !== 'REFUND_REQUEST') {
      return true; // Not applicable for other message types
    }

    if (!this.providerAutocomplete) {
      return false; // Provider autocomplete component is required
    }

    const searchType = this.providerAutocomplete.providerSearchType;
    const hasSelectedProvider = !!this.newMessageForm.healthProEyoneExternalId;
    const searchTerm = this.providerAutocomplete.searchTerm || '';
    const hasSearchTerm = searchTerm.trim().length >= 2;
    const hasSelectedProviderInAutocomplete = !!this.providerAutocomplete.selectedProvider;
    
    // Check if input was manually cleared (backspace scenario)
    const inputCleared = searchTerm.trim().length === 0;

    if (searchType === 'conventionnes') {
      // For conventionnés providers, a provider must be selected from the list
      // AND the input must not be manually cleared
      return hasSelectedProvider && hasSelectedProviderInAutocomplete && !inputCleared;
    } else if (searchType === 'non_conventionnes') {
      // For non conventionnés providers, either:
      // 1. A provider is selected from the list (and input not cleared), OR
      // 2. A search term is typed (will trigger validation modal on submit)
      return (hasSelectedProvider && hasSelectedProviderInAutocomplete && !inputCleared) || hasSearchTerm;
    }

    return false;
  }

  isFormValid(): boolean {
    // Vérifier qu'il n'y a pas de fichiers en cours d'upload
    const noUploadsInProgress = !this.uploadedFiles.some(f => f.isUploading);
    
    // Vérifier qu'il n'y a pas d'erreurs d'upload
    const noUploadErrors = !this.uploadedFiles.some(f => f.error);

    // Content optionnel pour demande de prise en charge et demande de remboursement
    const contentOptional = ['CARE_REQUEST', 'REFUND_REQUEST'].includes(this.newMessageForm.messageType);
    const contentValid = contentOptional || (this.newMessageForm.content && this.newMessageForm.content.trim().length > 0);

    return !!(
      this.newMessageForm.messageType &&
      contentValid &&
      (this.newMessageForm.messageType !== 'CARE_REQUEST' ||
        (this.newMessageForm.beneficiaryPasspport &&
          this.newMessageForm.healthProEyoneExternalId)) &&
      // INSURANCE_IDENTIFICATION_NUM_MAJ ne nécessite pas de bénéficiaire mais nécessite un numéro de téléphone valide
      (this.newMessageForm.messageType !== 'INSURANCE_IDENTIFICATION_NUM_MAJ' ||
        this.isValidPhoneNumber(this.newMessageForm.content)) &&
      // IDENTIFICATION_PHOTO_MAJ nécessite un bénéficiaire ET un fichier sélectionné
      (this.newMessageForm.messageType !== 'IDENTIFICATION_PHOTO_MAJ' ||
        (this.newMessageForm.beneficiaryPasspport && this.uploadedFiles.length > 0)) &&
      // For REFUND_REQUEST, ensure beneficiary is selected AND provider is properly selected/typed
      (this.newMessageForm.messageType !== 'REFUND_REQUEST' ||
        (this.newMessageForm.beneficiaryPasspport && this.isRefundRequestProviderValid())) &&
      // Vérifier qu'il n'y a pas d'uploads en cours ni d'erreurs
      noUploadsInProgress &&
      noUploadErrors
    );
  }

  // Méthode pour vérifier si le type de message permet l'attachement de fichiers
  canAttachFile(): boolean {
    return ['QUESTION', 'QUOTE', 'CARE_REQUEST'].includes(
      this.newMessageForm.messageType
    );
  }


  loadDocuments(contractId: string): void {
    if (!contractId) return;

    this.insuranceService.getInsuranceDocuments(contractId).subscribe({
      next: (response) => {
        this.documents = response;
      },
      error: (error) => {
        console.error('Error loading documents:', error);
        this.documents = [];
      },
    });
  }

  // Méthode pour valider le numéro de téléphone (Sénégal et Gabon uniquement)
  isValidPhoneNumber(phoneNumber: string): boolean {
    if (!phoneNumber) return false;

    // Vérifier que le numéro commence par les indicatifs autorisés
    const senegalCode = '+221';
    const gabonCode = '+241';

    return (
      phoneNumber.startsWith(senegalCode) || phoneNumber.startsWith(gabonCode)
    );
  }

  // Méthode pour extraire le numéro de téléphone sans l'indicatif
  extractPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';

    // Supprimer l'indicatif +221 ou +241
    const senegalCode = '+221';
    const gabonCode = '+241';

    if (phoneNumber.startsWith(senegalCode)) {
      return phoneNumber.substring(senegalCode.length);
    } else if (phoneNumber.startsWith(gabonCode)) {
      return phoneNumber.substring(gabonCode.length);
    }

    return phoneNumber; // Retourner le numéro tel quel si pas d'indicatif reconnu
  }

  // Méthode pour gérer le changement de type de message
  onMessageTypeChange(): void {
    // Nettoyer les champs selon le type de message sélectionné
    switch (this.newMessageForm.messageType) {
      case 'INSURANCE_IDENTIFICATION_NUM_MAJ':
        // Ce type ne nécessite pas de bénéficiaire ni de prestataire de santé
        this.newMessageForm.beneficiaryPasspport = '';
        this.newMessageForm.healthProEyoneExternalId = '';
        this.newMessageForm.healthProEyoneName = '';
        this.clearSelectedFile();
        break;
      case 'IDENTIFICATION_PHOTO_MAJ':
        // Ce type nécessite un bénéficiaire mais pas de prestataire de santé
        this.newMessageForm.healthProEyoneExternalId = '';
        this.newMessageForm.healthProEyoneName = '';
        this.clearSelectedFile();
        break;
      case 'QUESTION':
      case 'QUOTE':
        // Ces types ne nécessitent pas de bénéficiaire ni de prestataire de santé
        this.newMessageForm.beneficiaryPasspport = '';
        this.newMessageForm.healthProEyoneExternalId = '';
        this.newMessageForm.healthProEyoneName = '';
        // Ne pas vider les fichiers pour ces types car ils permettent l'attachement
        break;
      case 'CARE_REQUEST':
      case 'REFUND_REQUEST':
        // Ce type nécessite à la fois un bénéficiaire et un prestataire de santé
        // Ne pas nettoyer ces champs
        break;
    }
  }

  submitNewMessage(): void {
    if (!this.isFormValid() || !this.contractId) return;

    // Check if validation is needed for non-conventionnés providers
    // Only open modal if no provider has been selected from the list
    if (this.providerAutocomplete && this.providerAutocomplete.needsValidation()) {
      this.providerAutocomplete.triggerValidation();
      return; // Stop here, validation modal will handle the rest
    }

    // Handle case where healthProEyoneExternalId is null but we have a search term for REFUND_REQUEST
    // Only open modal if no provider was selected from non-conventionnés list
    if (
      this.newMessageForm.messageType === 'REFUND_REQUEST' &&
      !this.newMessageForm.healthProEyoneExternalId &&
      this.providerAutocomplete &&
      this.providerAutocomplete.searchTerm &&
      this.providerAutocomplete.providerSearchType === 'non_conventionnes' &&
      !this.providerAutocomplete.selectedProvider // Only if no provider selected
    ) {
      // Use the search term as healthProName
      this.newMessageForm.healthProEyoneName = this.providerAutocomplete.searchTerm;
      // Open validation modal to get provider type
      this.onValidationRequested({ searchTerm: this.providerAutocomplete.searchTerm });
      return;
    }

    // Activer l'animation de chargement
    this.sendingMessage = true;

    // D'abord uploader tous les fichiers, puis envoyer le message
    this.uploadAllFiles()
      .then(() => {
        // Tous les fichiers sont uploadés, maintenant envoyer le message
    // Préparer le contenu du message (optionnel pour CARE_REQUEST et REFUND_REQUEST)
    let messageContent = (this.newMessageForm.content || '').trim();

    // Si c'est une demande de changement de numéro de téléphone, extraire le numéro sans l'indicatif
    if (
      this.newMessageForm.messageType === 'INSURANCE_IDENTIFICATION_NUM_MAJ'
    ) {
      messageContent = this.extractPhoneNumber(this.newMessageForm.content);
    }

    // Create message data according to backend DTO
    // Collecter tous les _id des fichiers uploadés dans un tableau simple
    const uploadedDocumentIds = this.uploadedFiles
      .filter(f => f.isUploaded && f.uploadedDocumentId)
      .map(f => f.uploadedDocumentId!);
    
    console.log('Uploaded document IDs array:', uploadedDocumentIds);

    const messageData: CreateInsuranceMessageDto = {
      content: messageContent || '',
      messageType: this.newMessageForm.messageType as any,
      messageActorType: this.newMessageForm.messageActorType,
      destinaryType: this.newMessageForm.destinaryType,
      beneficiaryPasspport:
        this.newMessageForm.beneficiaryPasspport ||
        this.currentUser?.passportNumber ||
        undefined,
      healthProEyoneExternalId:
        this.newMessageForm.healthProEyoneExternalId || undefined,
      healthProEyoneName: this.newMessageForm.healthProEyoneName || undefined,
      documents: uploadedDocumentIds.length > 0 ? uploadedDocumentIds : undefined,
    };

    // Les fichiers sont déjà uploadés séparément, donc on envoie un JSON
    // Call the insurance service to send the message as JSON
    this.insuranceService
      .createMessage(this.contractId, messageData)
      .subscribe({
        next: (response) => {
          this.documents = [];
          // Désactiver l'animation de chargement
          this.sendingMessage = false;

          // Afficher un message de succès
          this.swalService.success('Succès', 'Message envoyé avec succès !');

          // Rafraîchir la liste des discussions et messages
          this.refreshDataAfterNewMessage(response?._id);

          // Reset form and hide it
          this.resetForm();
        },
        error: (error) => {
          // Désactiver l'animation de chargement
          this.sendingMessage = false;

          console.error('Error sending message:', error);

          // Afficher un toast d'erreur
          let errorMessage = "Erreur lors de l'envoi du message";
          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.swalService.error('Erreur', errorMessage);
        },
          });
      })
      .catch((error) => {
        // Erreur lors de l'upload des fichiers
        this.sendingMessage = false;
        console.error('Error uploading files:', error);
        this.swalService.error('Erreur', 'Erreur lors de l\'upload des fichiers. Veuillez réessayer.');
      });
  }

  /**
   * Rafraîchit les données après l'envoi d'un nouveau message
   */
  private refreshDataAfterNewMessage(preferredDiscussionId?: string | null): void {
    // Reset pagination
    this.currentPage = 1;
    this.hasMoreDiscussions = false;
    this.pendingDiscussionSelectionId = preferredDiscussionId ?? null;

    // Recharger les messages et discussions
    this.loadMessages(preferredDiscussionId ?? undefined);
    this.showNewMessageForm = false;
  }

  /**
   * Rafraîchit manuellement toutes les données (messages et discussions)
   */
  refreshData(): void {
    this.refreshing = true;
    this.currentPage = 1; // Reset pagination
    this.hasMoreDiscussions = false; // Reset pagination state
    this.loadMessages();
    this.loadMessageTypes();
  }

  /**
   * Calcule la taille totale des fichiers sélectionnés
   */
  getTotalFilesSize(): number {
    return this.uploadedFiles.reduce((total, fileTracker) => total + fileTracker.file.size, 0);
  }

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Constante pour la limite totale (10 Mo)
      const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 Mo en bytes

      // Calculer la taille totale actuelle des fichiers déjà uploadés ou en cours
      let currentTotalSize = this.uploadedFiles.reduce((total, f) => total + f.file.size, 0);

      // Pour les photos d'identité, ne permettre qu'un seul fichier
      if (this.newMessageForm.messageType === 'IDENTIFICATION_PHOTO_MAJ') {
        // Sauvegarder le fichier AVANT de vider les fichiers précédents
        // car clearSelectedFile() réinitialise l'input file
        const file = files[0];

        // Vérifier que le fichier existe
        if (!file) {
          console.error('No file selected');
          return;
        }
        
        // Vider les fichiers précédents (cela réinitialisera aussi l'input)
        this.clearSelectedFile();

        // Vérifier la taille du fichier (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          this.swalService.error(
            'Fichier trop volumineux',
            `Le fichier "${file.name}" est trop volumineux. Taille maximum autorisée : 5MB`
          );
          return;
        }

        // Vérifier que la taille totale ne dépasse pas 10 Mo
        if (file.size > MAX_TOTAL_SIZE) {
          this.swalService.error(
            'Fichier trop volumineux',
            `Le fichier "${file.name}" est trop volumineux. Taille maximum totale autorisée : 10MB`
          );
          return;
        }

        // Vérifier le type de fichier (seules les images sont acceptées)
        if (!file.type.startsWith('image/')) {
          this.swalService.error(
            'Type de fichier invalide',
            `Veuillez sélectionner un fichier image valide pour "${file.name}"`
          );
          return;
        }

        // Ajouter le fichier à la liste
        this.selectedFiles.push(file);
        this.selectedFileNames.push(file.name);

        // Stocker le nom du fichier dans le contenu
        this.newMessageForm.content = file.name;

        // Ajouter le fichier à la liste de suivi sans uploader immédiatement
        this.addFileToTracker(file);
        console.log('File selected for IDENTIFICATION_PHOTO_MAJ:', {
          fileName: file.name,
          fileSize: file.size,
          uploadedFilesCount: this.uploadedFiles.length,
          uploadedFiles: this.uploadedFiles.map(f => ({ id: f.id, name: f.file.name }))
        });
      } else {
        // Pour les autres types de messages, permettre plusieurs fichiers
        // Vérifier chaque fichier
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          
          // Vérifier que le fichier existe
          if (!file) {
            console.error(`File at index ${i} is undefined`);
            continue;
          }

          // Vérifier la taille du fichier (5MB max)
          if (file.size > 5 * 1024 * 1024) {
            this.swalService.error(
              'Fichier trop volumineux',
              `Le fichier "${file.name}" est trop volumineux. Taille maximum autorisée : 5MB`
            );
            continue;
          }

          // Vérifier que la somme totale ne dépasse pas 10 Mo
          const newTotalSize = currentTotalSize + file.size;
          if (newTotalSize > MAX_TOTAL_SIZE) {
            this.swalService.error(
              'Limite de taille dépassée',
              `La somme totale des fichiers ne doit pas dépasser 10 Mo. Taille actuelle : ${this.formatFileSize(currentTotalSize)}, nouveau fichier : ${this.formatFileSize(file.size)}`
            );
            continue;
          }

          // Vérifier les types de fichiers acceptés
          if (this.canAttachFile()) {
            const allowedTypes = [
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'image/jpeg',
              'image/jpg',
              'image/png',
            ];

            if (!allowedTypes.includes(file.type)) {
              this.swalService.error(
                'Type de fichier invalide',
                `Veuillez sélectionner un fichier valide (PDF, DOC, DOCX, JPG, PNG) pour "${file.name}"`
              );
              continue;
            }
          }

          // Ajouter le fichier à la liste
          this.selectedFiles.push(file);
          this.selectedFileNames.push(file.name);
          
          // Mettre à jour la taille totale pour le prochain fichier
          currentTotalSize = newTotalSize;

          // Ajouter le fichier à la liste de suivi sans uploader immédiatement
          this.addFileToTracker(file);
        }
      }
    }
  }

  clearSelectedFile(fileId?: string): void {
    if (fileId) {
      // Supprimer un fichier spécifique
      const fileIndex = this.uploadedFiles.findIndex(f => f.id === fileId);
      if (fileIndex !== -1) {
        // Annuler l'upload si en cours
        // Note: HttpClient n'a pas de méthode abort directe, mais on peut marquer comme annulé
        const file = this.uploadedFiles[fileIndex];
        if (file.isUploading) {
          file.isUploading = false;
          file.error = 'Téléchargement annulé';
        }
        
        // Libérer l'URL de preview si c'est une image
        if (file.previewUrl && file.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(file.previewUrl);
        }
        
        this.uploadedFiles.splice(fileIndex, 1);
        
        // Mettre à jour selectedFiles et selectedFileNames
        this.selectedFiles = this.selectedFiles.filter(f => f !== file.file);
        this.selectedFileNames = this.selectedFileNames.filter(name => name !== file.file.name);
      }
    } else {
      // Vider tous les fichiers
      this.uploadedFiles.forEach(file => {
        if (file.previewUrl && file.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      
      this.selectedFileName = '';
      this.selectedFile = null;
      this.selectedFiles = [];
      this.selectedFileNames = [];
      this.uploadedFiles = [];
    }
    
    // Réinitialiser l'input file - trouver tous les inputs file et les réinitialiser
    const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
    fileInputs.forEach(input => {
      input.value = '';
    });

    // Si c'était une photo d'identité, ne pas vider le contenu car il sera rempli avec le nom du nouveau fichier
    // Le contenu sera mis à jour dans onFileSelected
  }

  /**
   * Obtient le type de document selon le type de message
   */
  private getDocumentTypeForMessageType(messageType: string): string {
    if (!messageType) {
      console.warn('messageType is empty, using default MEDICAL_REPORT');
      return 'MEDICAL_REPORT';
    }
    
    switch (messageType) {
      case 'IDENTIFICATION_PHOTO_MAJ':
        return 'IDENTITY_DOCUMENT';
      case 'CARE_REQUEST':
      case 'REFUND_REQUEST':
        return 'MEDICAL_REPORT';
      case 'QUESTION':
      case 'QUOTE':
        return 'INVOICE';
      default:
        console.warn('Unknown messageType:', messageType, 'using default MEDICAL_REPORT');
        return 'MEDICAL_REPORT';
    }
  }

  /**
   * Ajoute un fichier à la liste de suivi sans l'uploader immédiatement
   */
  private addFileToTracker(file: File): void {
    const fileId = `${Date.now()}-${Math.random()}`;
    
    // Créer une URL de preview pour les images
    let previewUrl: string | undefined;
    if (file.type.startsWith('image/')) {
      previewUrl = URL.createObjectURL(file);
    }

    // Ajouter le fichier à la liste de suivi sans uploader
    const fileTracker: {
      id: string;
      file: File;
      progress: number;
      isUploading: boolean;
      isUploaded: boolean;
      uploadedDocumentId?: string;
      uploadedUrl?: string;
      error?: string;
      previewUrl?: string;
    } = {
      id: fileId,
      file: file,
      progress: 0,
      isUploading: false,
      isUploaded: false,
      previewUrl: previewUrl
    };
    this.uploadedFiles.push(fileTracker);
    console.log('File added to tracker:', { fileName: file.name, fileId, uploadedFilesCount: this.uploadedFiles.length });
    // Forcer la détection de changement pour que le template se mette à jour
    this.cdr.detectChanges();
  }

  /**
   * Upload tous les fichiers non uploadés
   * Retourne une Promise qui se résout quand tous les uploads sont terminés
   */
  private uploadAllFiles(): Promise<void> {
    return new Promise((resolve, reject) => {
      const filesToUpload = this.uploadedFiles.filter(f => !f.isUploaded && !f.isUploading && !f.error);
      
      if (filesToUpload.length === 0) {
        resolve();
        return;
      }

      let completedUploads = 0;
      let hasError = false;

      filesToUpload.forEach((fileTracker) => {
        // Obtenir le passportNumber
        let passportNumber = this.newMessageForm.beneficiaryPasspport || 
                             this.currentUser?.passportNumber || 
                             '';
        
        if (!passportNumber) {
          fileTracker.error = 'Numéro de passeport requis pour l\'upload';
          hasError = true;
          completedUploads++;
          if (completedUploads === filesToUpload.length) {
            if (hasError) {
              reject(new Error('Erreur lors de l\'upload des fichiers'));
            } else {
              resolve();
            }
          }
          return;
        }

        // Marquer comme en cours d'upload
        fileTracker.isUploading = true;
        fileTracker.progress = 0;

        // Préparer les données d'upload
        const documentType = this.insuranceDocumentTypeId || 'INSURANCE_DOCUMENT';
        const documentData: CreateDocumentDto = {
          documentType: documentType,
          contractId: this.contractId || undefined,
          file: fileTracker.file
        };

        // Uploader le fichier
        this.documentService.uploadDocument(passportNumber, documentData, true).subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              if (event.total) {
                const progress = Math.round((100 * event.loaded) / event.total);
                fileTracker.progress = progress;
              }
            } else if (event.type === HttpEventType.Response) {
              // Upload terminé avec succès
              fileTracker.isUploading = false;
              fileTracker.isUploaded = true;
              fileTracker.progress = 100;
              
              // Extraire l'ID du document de la réponse
              if (event.body && event.body.data) {
                if (event.body.data._id) {
                  fileTracker.uploadedDocumentId = event.body.data._id;
                }
                if (event.body.data.url) {
                  fileTracker.uploadedUrl = event.body.data.url;
                  if (fileTracker.file.type.startsWith('image/') && fileTracker.previewUrl && fileTracker.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(fileTracker.previewUrl);
                    fileTracker.previewUrl = event.body.data.url;
                  }
                }
              } else if (event.body) {
                if (event.body.documentId) {
                  fileTracker.uploadedDocumentId = event.body.documentId;
                }
                if (event.body.url) {
                  fileTracker.uploadedUrl = event.body.url;
                  if (fileTracker.file.type.startsWith('image/') && fileTracker.previewUrl && fileTracker.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(fileTracker.previewUrl);
                    fileTracker.previewUrl = event.body.url;
                  }
                }
              }

              completedUploads++;
              if (completedUploads === filesToUpload.length) {
                if (hasError) {
                  reject(new Error('Erreur lors de l\'upload des fichiers'));
                } else {
                  resolve();
                }
              }
            }
          },
          error: (error) => {
            console.error('Error uploading file:', error);
            fileTracker.isUploading = false;
            fileTracker.isUploaded = false;
            fileTracker.error = error.error?.message || 'Erreur lors de l\'upload du fichier';
            hasError = true;
            
            completedUploads++;
            if (completedUploads === filesToUpload.length) {
              reject(new Error('Erreur lors de l\'upload des fichiers'));
            }
          }
        });
      });
    });
  }

  /**
   * Upload un fichier immédiatement après sa sélection (méthode conservée pour compatibilité)
   */
  private uploadFile(file: File): void {
    const fileId = `${Date.now()}-${Math.random()}`;
    
    console.log('uploadFile called:', {
      fileName: file.name,
      fileSize: file.size,
      messageType: this.newMessageForm.messageType,
      beneficiaryPasspport: this.newMessageForm.beneficiaryPasspport,
      currentUser: this.currentUser,
      currentUserPassportNumber: this.currentUser?.passportNumber
    });
    
    // Essayer d'obtenir le passportNumber de différentes sources
    // Pour certains types de messages (QUESTION, QUOTE), le bénéficiaire n'est pas requis
    // On utilise donc le currentUser par défaut
    let passportNumber = this.newMessageForm.beneficiaryPasspport || 
                         this.currentUser?.passportNumber || 
                         '';
    
    // Si toujours pas de passportNumber, essayer de charger currentUser
    if (!passportNumber) {
      console.warn('PassportNumber not found immediately. Checking currentUser subscription...');
      
      // Si currentUser n'est pas encore chargé, attendre un peu
      if (!this.currentUser) {
        // Essayer de charger currentUser depuis le store
        this.store.select(selectCurrentUser).pipe(
          take(1) // Prendre seulement la première valeur
        ).subscribe((user) => {
          if (user) {
            this.currentUser = user;
            passportNumber = this.newMessageForm.beneficiaryPasspport || 
                           this.currentUser?.passportNumber || 
                           '';
            if (passportNumber) {
              console.log('PassportNumber found after waiting:', passportNumber);
              this.executeUpload(file, fileId, passportNumber);
            } else {
              console.error('PassportNumber still not found after loading currentUser');
              this.swalService.error('Erreur', 'Numéro de passeport requis pour l\'upload. Veuillez vous assurer que vous êtes connecté.');
            }
          } else {
            console.error('CurrentUser is null');
            this.swalService.error('Erreur', 'Vous devez être connecté pour uploader des fichiers.');
          }
        });
      } else {
        // currentUser existe mais n'a pas de passportNumber
        console.error('CurrentUser exists but has no passportNumber');
        this.swalService.error('Erreur', 'Numéro de passeport requis pour l\'upload');
      }
      return;
    }

    console.log('PassportNumber found:', passportNumber);
    this.executeUpload(file, fileId, passportNumber);
  }

  private executeUpload(file: File, fileId: string, passportNumber: string): void {
    console.log('Starting file upload:', { fileName: file.name, passportNumber });

    // Créer une URL de preview pour les images
    let previewUrl: string | undefined;
    if (file.type.startsWith('image/')) {
      previewUrl = URL.createObjectURL(file);
    }

    // Ajouter le fichier à la liste de suivi
    const fileTracker: {
      id: string;
      file: File;
      progress: number;
      isUploading: boolean;
      isUploaded: boolean;
      uploadedDocumentId?: string;
      uploadedUrl?: string;
      error?: string;
      previewUrl?: string;
    } = {
      id: fileId,
      file: file,
      progress: 0,
      isUploading: true,
      isUploaded: false,
      previewUrl: previewUrl
    };
    this.uploadedFiles.push(fileTracker);

    // Préparer les données d'upload
    // Utiliser l'ID du type INSURANCE_DOCUMENT si disponible, sinon utiliser le code INSURANCE_DOCUMENT comme fallback
    const documentType = this.insuranceDocumentTypeId || 'INSURANCE_DOCUMENT';
    const documentData: CreateDocumentDto = {
      documentType: documentType,
      contractId: this.contractId || undefined,
      file: file
    };

    console.log('Upload document data:', { 
      documentType, 
      contractId: this.contractId, 
      fileName: file.name,
      passportNumber,
      fileSize: file.size
    });

    // Uploader le fichier avec suivi de progression
    console.log('Calling uploadDocument service...');
    this.documentService.uploadDocument(passportNumber, documentData, true).subscribe({
      next: (event) => {
        console.log('Upload event:', event);
        if (event.type === HttpEventType.UploadProgress) {
          // Calculer le pourcentage de progression
          if (event.total) {
            const progress = Math.round((100 * event.loaded) / event.total);
            fileTracker.progress = progress;
            console.log(`Upload progress: ${progress}%`);
          }
        } else if (event.type === HttpEventType.Response) {
          // Upload terminé avec succès
          console.log('Upload completed:', event.body);
          fileTracker.isUploading = false;
          fileTracker.isUploaded = true;
          fileTracker.progress = 100;
          
          // Extraire l'ID du document de la réponse (structure: { status: "success", data: { _id: "...", url: "..." } })
          if (event.body && event.body.data) {
            // Nouvelle structure de réponse avec data._id
            if (event.body.data._id) {
              fileTracker.uploadedDocumentId = event.body.data._id;
              console.log('Document _id extracted:', event.body.data._id);
            }
            if (event.body.data.url) {
              fileTracker.uploadedUrl = event.body.data.url;
              // Remplacer la preview blob par l'URL réelle si c'est une image
              if (file.type.startsWith('image/') && fileTracker.previewUrl) {
                URL.revokeObjectURL(fileTracker.previewUrl);
                fileTracker.previewUrl = event.body.data.url;
              }
            }
          }
          // Fallback pour l'ancienne structure de réponse
          else if (event.body) {
            if (event.body.documentId) {
              fileTracker.uploadedDocumentId = event.body.documentId;
            }
            if (event.body.url) {
              fileTracker.uploadedUrl = event.body.url;
              // Remplacer la preview blob par l'URL réelle si c'est une image
              if (file.type.startsWith('image/') && fileTracker.previewUrl) {
                URL.revokeObjectURL(fileTracker.previewUrl);
                fileTracker.previewUrl = event.body.url;
              }
            }
          }
        }
      },
      error: (error) => {
        console.error('Error uploading file:', error);
        fileTracker.isUploading = false;
        fileTracker.isUploaded = false;
        fileTracker.error = error.error?.message || 'Erreur lors de l\'upload du fichier';
        this.swalService.error('Erreur', `Échec de l'upload: ${fileTracker.error}`);
      }
    });
  }

  continueSubmission(): void {
    // This method is called after validation is complete
    // Simply call submitNewMessage again, but validation won't be needed this time
    this.submitNewMessage();
  }

  /**
   * Fait défiler le conteneur des discussions vers le bas
   */
  private scrollToBottom(): void {
    // Attendre que le DOM soit mis à jour
    setTimeout(() => {
      const discussionsContainer = document.querySelector(
        '.discussions-scroll'
      ) as HTMLElement;
      if (discussionsContainer) {
        discussionsContainer.scrollTop = discussionsContainer.scrollHeight;
      }
    }, 100);
  }

  /**
   * Vérifie si un fichier est une image basé sur son extension
   */
  isImageFile(filename: string): boolean {
    if (!filename) return false;
    const extension = filename.split('.').pop()?.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return imageExtensions.includes(extension || '');
  }

  /**
   * Retourne l'icône appropriée selon l'extension du fichier
   */
  getFileIcon(filename: string): any {
    if (!filename) return this.File;
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return this.FileText;
      case 'doc':
      case 'docx':
        return this.FileText;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'svg':
        return this.ImageIcon;
      default:
        return this.File;
    }
  }

  /**
   * Formate la taille du fichier en format lisible
   */
  formatFileSize(sizeBytes: number): string {
    if (!sizeBytes) return '0 B';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = sizeBytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
}
